import { useEffect, useLayoutEffect, useRef, useState } from "react";
import "../styles/energyflow.css";

import solar from "../assets/energy/solar-panel.png";
import inverter from "../assets/energy/hybrid-inverter.png";
import battery from "../assets/energy/battery.png";
import house from "../assets/energy/house.png";
import grid from "../assets/energy/grid.png";
import { FaTimes } from "react-icons/fa";

// Anything below this many watts counts as "no real flow" —
// the pipe stays put instead of animating.
const FLOW_THRESHOLD = 10;

export default function EnergyFlow({

    pvPower = 0,
    pvVoltage = 0,
    pvCurrent = 0,

    loadPower = 0,
    outputVoltage = 0,
    outputFrequency = 0,
    loadCurrent = 0,
    loadPer = 0,

    // null/undefined = no battery hardware fitted at all.
    // A real 0 means a battery IS fitted but reads empty right now -
    // those are two different situations and shouldn't look the same.
    batterySOC = null,
    batteryPower = 0,
    batteryVoltage = 0,
    batteryCurrent = 0,

    gridPower = 0,
    gridVoltage = 0,
    gridCurrent = 0,
    gridFrequency = 0

}) {

    const idRef = useRef(`ef-${Math.random().toString(36).slice(2, 9)}`);
    const [currentTime, setCurrentTime] = useState(new Date());
    const containerRef = useRef(null);
    const solarAnchor = useRef(null);
    const loadAnchor = useRef(null);
    const batteryAnchor = useRef(null);
    const gridAnchor = useRef(null);
    const invTopAnchor = useRef(null);
    const invBottomAnchor = useRef(null);

    const [geo, setGeo] = useState(null);

    const hasBattery = batterySOC !== null && batterySOC !== undefined;

    useEffect(() => {

        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);

    }, []);

    // ---------------------------------------------------------
    // Measure the real rendered position of every node anchor
    // and turn them into pixel-accurate connector paths.
    // Re-runs whenever the layout resizes (window, sidebar
    // opening, breakpoint switch, images finishing load, etc) -
    // this is what makes the diagram line up correctly on any
    // screen size without any hand-tuned per-device offsets.
    // ---------------------------------------------------------
    const measure = () => {

        const cont = containerRef.current;

        if (!cont) return;

        // Schematic is hidden on mobile - nothing to measure.
        if (cont.offsetParent === null) return;

        const box = cont.getBoundingClientRect();

        const point = (ref) => {
            const r = ref.current.getBoundingClientRect();
            return {
                x: r.left + r.width / 2 - box.left,
                y: r.top + r.height / 2 - box.top
            };
        };

        const solarP = point(solarAnchor);
        const loadP = point(loadAnchor);
        const invTop = point(invTopAnchor);
        const invBottom = point(invBottomAnchor);

        // Two separate trunks either side of center so the
        // solar/load (and battery/grid) lines never overlap.
        const topLeftTrunk = invTop.x - 14;
        const topRightTrunk = invTop.x + 14;
        const botLeftTrunk = invBottom.x - 14;
        const botRightTrunk = invBottom.x + 14;

        const next = {
            width: box.width,
            height: box.height,

            // solar -> inverter (solar only ever sources)
            solar: `M ${solarP.x} ${solarP.y} L ${topLeftTrunk} ${solarP.y} L ${topLeftTrunk} ${invTop.y}`,

            // inverter -> load (load only ever consumes)
            load: `M ${topRightTrunk} ${invTop.y} L ${topRightTrunk} ${loadP.y} L ${loadP.x} ${loadP.y}`
        };

        if (hasBattery) {

            const batteryP = point(batteryAnchor);

            // inverter -> battery by default (charging); reversed = discharging
            next.battery = `M ${botLeftTrunk} ${invBottom.y} L ${botLeftTrunk} ${batteryP.y} L ${batteryP.x} ${batteryP.y}`;

        }

        const gridP = point(gridAnchor);

        // inverter -> grid by default (exporting); reversed = importing
        next.grid = `M ${botRightTrunk} ${invBottom.y} L ${botRightTrunk} ${gridP.y} L ${gridP.x} ${gridP.y}`;

        setGeo(next);

    };

    useLayoutEffect(() => {

        measure();

        const ro = new ResizeObserver(() => measure());
        if (containerRef.current) ro.observe(containerRef.current);

        window.addEventListener("resize", measure);
        window.addEventListener("orientationchange", measure);

        return () => {
            ro.disconnect();
            window.removeEventListener("resize", measure);
            window.removeEventListener("orientationchange", measure);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasBattery]);

    // ---------------- Real flow direction / activity ----------------
    const solarActive = pvPower > FLOW_THRESHOLD;
    const loadActive = loadPower > FLOW_THRESHOLD;

    const batteryActive = hasBattery && Math.abs(batteryPower) > FLOW_THRESHOLD;
    const batteryDischarging = batteryPower < 0; // battery -> inverter

    const gridActive = Math.abs(gridPower) > FLOW_THRESHOLD;
    const gridImporting = gridPower > 0; // grid -> inverter

    const id = idRef.current;

    const formattedDate = currentTime.toLocaleDateString("en-US", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric"
    });

    const formattedTime = currentTime.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
    });

    const paths = [
        { key: "solar", d: geo?.solar, cls: "solarFlow", active: solarActive, reversed: false },
        { key: "load", d: geo?.load, cls: "loadFlow", active: loadActive, reversed: false },
        { key: "grid", d: geo?.grid, cls: "gridFlow", active: gridActive, reversed: gridImporting }
    ];

    if (hasBattery) {
        paths.push({
            key: "battery", d: geo?.battery, cls: "batteryFlow",
            active: batteryActive, reversed: batteryDischarging
        });
    }

    return (

        <div className="energyCanvas">

            {/* ---------- Panel title ---------- */}
            <div className="flowTitle">

                <span className="liveDot" />

                <div className="titleContent">

                    <h2>REAL TIME DATA</h2>

                    <div className="currentDateTime">
                        <span className="currentDate">{formattedDate}</span>
                        <span className="separator"> | </span>
                        <span className="currentTime">{formattedTime}</span>
                    </div>

                </div>

            </div>

            {/* ---------- Schematic (desktop / tablet) ---------- */}
            <div className="flowGrid" ref={containerRef}>

                {geo && (

                    <svg
                        className="flowSvg"
                        viewBox={`0 0 ${geo.width} ${geo.height}`}
                    >

                        {paths.map(p => p.d && (

                            <g key={p.key}>

                                <path
                                    id={`${id}-${p.key}`}
                                    className={
                                        `flowPath ${p.cls}` +
                                        (p.active ? " flowActive" : "") +
                                        (p.active && p.reversed ? " flowReverse" : "")
                                    }
                                    d={p.d}
                                />

                                {p.active && (
                                    <circle r="3.2" className={`flowDot ${p.cls}`}>
                                        <animateMotion
                                            dur="1.4s"
                                            repeatCount="indefinite"
                                            keyPoints={p.reversed ? "1;0" : "0;1"}
                                            keyTimes="0;1"
                                            calcMode="linear"
                                        >
                                            <mpath href={`#${id}-${p.key}`} />
                                        </animateMotion>
                                    </circle>
                                )}

                            </g>

                        ))}

                    </svg>

                )}

                {/* Solar */}
                <div className="solarNode">

                    <div className="nodeStats right">
                        <div className="statLine"><span>Power</span><strong>{pvPower} W</strong></div>
                        <div className="statLine"><span>Voltage</span><strong>{pvVoltage} V</strong></div>
                        <div className="statLine"><span>Current</span><strong>{pvCurrent} A</strong></div>
                    </div>

                    <div className="nodeIcon">
                        <img src={solar} alt="" onLoad={measure} />
                        <span className="lineAnchor right" ref={solarAnchor} />
                        <h3>Solar</h3>
                    </div>

                </div>

                {/* Load */}
                <div className="loadNode">

                    <div className="nodeIcon">
                        <img src={house} alt="" onLoad={measure} />
                        <span className="lineAnchor left" ref={loadAnchor} />
                        <h3>Load</h3>
                    </div>

                    <div className="nodeStats left">
                        <div className="statLine"><span>Power</span><strong>{loadPower} W</strong></div>
                        <div className="statLine"><span>Voltage</span><strong>{outputVoltage} V</strong></div>
                        <div className="statLine"><span>Frequency</span><strong>{outputFrequency} Hz</strong></div>
                        <div className="statLine"><span>Current</span><strong>{loadCurrent} A</strong></div>
                        <div className="statLine"><span>Load</span><strong>{loadPer} %</strong></div>
                    </div>

                </div>

                {/* Inverter */}
                <div className="inverterNode">
                    <span className="lineAnchor top" ref={invTopAnchor} />
                    <img src={inverter} alt="" onLoad={measure} />
                    <span className="lineAnchor bottom" ref={invBottomAnchor} />
                    <h2>Inverter</h2>
                </div>

                {/* Battery - icon column lines up with Solar's icon column.
                    No battery hardware fitted -> show a clear "not available"
                    state instead of a misleading 0%. */}
                <div className="batteryNode">

                    <div className="nodeStats right">
                        {hasBattery && (
                            <>
                                <div className="statLine"><span>Power</span><strong>{batteryPower} W</strong></div>
                                <div className="statLine"><span>Voltage</span><strong>{batteryVoltage} V</strong></div>
                                <div className="statLine"><span>Current</span><strong>{batteryCurrent} A</strong></div>
                            </>
                        )}
                    </div>

                    <div className="nodeIcon">

                        <div className={hasBattery ? "batteryImgWrap" : "batteryImgWrap unavailable"}>
                            <img src={battery} alt="" onLoad={measure} />
                            {!hasBattery && (
                                <span className="batteryXBadge"><FaTimes /></span>
                            )}
                        </div>

                        {hasBattery && (
                            <span className="lineAnchor right" ref={batteryAnchor} />
                        )}

                        <h3>Battery</h3>

                        {hasBattery ? (
                            <p>{batterySOC}%</p>
                        ) : (
                            <p className="batteryUnavailable">Not Available</p>
                        )}

                    </div>

                </div>

                {/* Grid */}
                <div className="gridNode">

                    <div className="nodeIcon">
                        <img src={grid} alt="" onLoad={measure} />
                        <span className="lineAnchor left" ref={gridAnchor} />
                        <h3>Grid</h3>
                    </div>

                    <div className="nodeStats left">
                        <div className="statLine"><span>Power</span><strong>{gridPower} W</strong></div>
                        <div className="statLine"><span>Voltage</span><strong>{gridVoltage} V</strong></div>
                        <div className="statLine"><span>Current</span><strong>{gridCurrent} A</strong></div>
                        <div className="statLine"><span>Frequency</span><strong>{gridFrequency} Hz</strong></div>
                    </div>

                </div>

            </div>

        </div>

    );

}
